import {
    FC,
    Key,
    ReactNode,
    useEffect,
    useState,
} from 'react';
import { Menu, MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '../../store/workspace.store';

type MenuItem = Required<MenuProps>['items'][number];

const SidebarMenu: FC = () => {
    const { t } = useTranslation('shared', { keyPrefix: 'PAGES' });

    const rootSubmenuKeys = ['BOOKINGS', 'SETTINGS'];

    const [openKeys, setOpenKeys] = useState(['HOME']);
    const [selectedKey, setSelectedKey] = useState();

    const loading = useWorkspaceStore((state) => state.loading);

    const navigate = useNavigate();
    const location = useLocation();

    const getItem = (
        label: ReactNode,
        key: Key,
        icon?: ReactNode,
        children?: MenuItem[],
        type?: 'group',
    ): MenuItem => ({
        key,
        icon,
        children,
        label,
        type,
    } as MenuItem);

    const items: MenuItem[] = [
        getItem(t('HOME'), '/'),
        getItem(t('SMALL_FLEET_ORDERS'), '/requests'),
        getItem(t('BOOKINGS'), '/bookings', null, [
            getItem(t('ACTIVE_BOOKINGS'), '/bookings/actives'),
            getItem(t('HISTORY_BOOKINGS'), '/bookings/history'),
        ]),
        getItem(t('DRIVERS'), '/drivers'),
    ];

    const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
        const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
        if (rootSubmenuKeys.indexOf(latestOpenKey!) === -1) {
            setOpenKeys(keys);
        } else {
            setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
        }
    };

    const handleMenuItemClick = (item: MenuItem) => {
        const pathMap: Record<string, string> = {
            '/': '/',
            '/requests': '/requests',
            '/bookings/actives': '/bookings/actives',
            '/bookings/history': '/bookings/history',
            '/drivers': '/drivers',
        };

        const newPath = pathMap[item?.key as any];
        if (newPath) navigate(newPath);
    };

    useEffect(() => {
        if (location.pathname === '/') {
            setSelectedKey(['/'] as any);
            return;
        }
        const newKey = items.find((item: any) => {
            if (item.key === '/') return false;

            if (item.children) {
                return item.children.find((child: any) => location.pathname.includes(child.key))?.key;
            }
            return location.pathname.includes(item.key);
        })?.key;
        setSelectedKey(newKey as any);
    }, [location.pathname]);

    return (
        <div className="sidebar__menu">
            <nav className="nav">
                <Menu
                    disabled={loading}
                    mode="inline"
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                    selectedKeys={selectedKey}
                    onClick={(item) => handleMenuItemClick(item as unknown as MenuItem)}
                    items={items}
                />
            </nav>
        </div>
    );
};

export { SidebarMenu };
